import { resolveAssetUrl } from './mediaStore';

/** Cached JPEG data-URLs keyed by assetId + time bucket (0.5s). */
const frameCache = new Map<string, string>();
const inflight = new Map<string, Promise<string | null>>();

let sharedVideo: HTMLVideoElement | null = null;
let queue: Promise<void> = Promise.resolve();

function getVideo(): HTMLVideoElement {
  if (!sharedVideo) {
    sharedVideo = document.createElement('video');
    sharedVideo.muted = true;
    sharedVideo.playsInline = true;
    sharedVideo.preload = 'auto';
  }
  return sharedVideo;
}

function bucketKey(assetId: string, time: number) {
  const b = Math.max(0, Math.floor(time * 2));
  return `${assetId}:${b}`;
}

function seekVideo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve) => {
    const t = Math.max(0, time);
    if (Math.abs(video.currentTime - t) < 0.05 && video.readyState >= 2) {
      resolve();
      return;
    }
    const done = () => {
      video.removeEventListener('seeked', done);
      resolve();
    };
    video.addEventListener('seeked', done);
    try {
      video.currentTime = t;
    } catch {
      resolve();
    }
    window.setTimeout(done, 400);
  });
}

function captureFrame(video: HTMLVideoElement, maxW = 80, maxH = 48): string | null {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) return null;
  const scale = Math.min(maxW / vw, maxH / vh, 1);
  const w = Math.max(1, Math.round(vw * scale));
  const h = Math.max(1, Math.round(vh * scale));
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.drawImage(video, 0, 0, w, h);
  try {
    return canvas.toDataURL('image/jpeg', 0.7);
  } catch {
    return null;
  }
}

async function extractFrame(
  asset: { id: string; url: string },
  time: number
): Promise<string | null> {
  const key = bucketKey(asset.id, time);
  const cached = frameCache.get(key);
  if (cached) return cached;

  const existing = inflight.get(key);
  if (existing) return existing;

  const p: Promise<string | null> = (async () => {
    const url = await resolveAssetUrl(asset);
    const video = getVideo();
    let dataUrl: string | null = null;

    const run = async () => {
      if (video.src !== url) {
        video.src = url;
        await new Promise<void>((resolve) => {
          const ready = () => {
            video.removeEventListener('loadeddata', ready);
            resolve();
          };
          video.addEventListener('loadeddata', ready);
          void video.load();
          window.setTimeout(resolve, 2000);
        });
      }
      const dur = Number.isFinite(video.duration) ? video.duration : time;
      await seekVideo(video, Math.min(time, Math.max(0, dur - 0.05)));
      dataUrl = captureFrame(video);
      if (dataUrl) frameCache.set(key, dataUrl);
    };

    queue = queue.then(run, run);
    await queue;
    return dataUrl;
  })().finally(() => inflight.delete(key));

  inflight.set(key, p);
  return p;
}

export function filmstripTimes(
  inPoint: number,
  duration: number,
  widthPx: number,
  tilePx = 56
): number[] {
  const count = Math.max(1, Math.ceil(widthPx / tilePx));
  const times: number[] = [];
  for (let i = 0; i < count; i++) {
    times.push(inPoint + ((i + 0.5) / count) * duration);
  }
  return times;
}

export async function getFilmstripFrames(
  asset: { id: string; url: string },
  times: number[]
): Promise<(string | null)[]> {
  const out: (string | null)[] = [];
  for (const t of times) {
    out.push(await extractFrame(asset, t));
  }
  return out;
}

/** Single poster frame for media bin / lists. */
export async function getPosterThumb(
  asset: { id: string; url: string; duration?: number },
  time?: number
): Promise<string | null> {
  const t =
    time ??
    Math.min(0.5, Math.max(0, ((asset.duration ?? 1) > 1 ? 0.5 : 0)));
  return extractFrame(asset, t);
}
